import React from 'react';
import { toast } from 'sonner';

const PROBE_TIMEOUT_MS = 5000;
const RECHECK_INTERVAL_MS = 15000;

async function checkRealConnectivity(): Promise<boolean> {
  if (!navigator.onLine) return false;
  const origin = typeof window !== 'undefined' ? window.location?.origin : '';
  if (!origin) return false;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const probeUrl = `${origin}/?connectivity=${Date.now()}`;
    const res = await fetch(probeUrl, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    return res.ok;
  } catch (err) {
    // The browser aborts in-flight HEADs during navigation, surfacing as
    // AbortError / DOMException. Treating that as "offline" was misleading
    // — it caused a red toast to fire mid-workflow when the user simply
    // routed from /workflows to /?planId=… Trust navigator.onLine in that
    // case; only return false on real network failure.
    const isAbort =
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof Error && err.name === 'AbortError');
    if (isAbort && navigator.onLine) return true;
    return false;
  }
}

export const NetworkStatusDialog: React.FC = () => {
  const [isOffline, setIsOffline] = React.useState(false);
  const recheckRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const toastIdRef = React.useRef<string | number | null>(null);

  const runCheck = React.useCallback(async () => {
    const online = await checkRealConnectivity();
    setIsOffline((prev) => (prev === !online ? prev : !online));
  }, []);

  React.useEffect(() => {
    runCheck();

    window.addEventListener('online', runCheck);
    window.addEventListener('offline', runCheck);
    recheckRef.current = setInterval(runCheck, RECHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', runCheck);
      window.removeEventListener('offline', runCheck);
      if (recheckRef.current) clearInterval(recheckRef.current);
    };
  }, [runCheck]);

  React.useEffect(() => {
    if (isOffline) {
      if (!toastIdRef.current) {
        toastIdRef.current = toast.error('You are offline', {
          description: 'Some features may not work until your connection is restored.',
          duration: Infinity,
          action: {
            label: 'Retry',
            onClick: () => runCheck(),
          },
        });
      }
    } else {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
        toast.success('Connection restored');
      }
    }
  }, [isOffline, runCheck]);

  return null;
};
